import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HiArrowLongRight, HiCheck } from "react-icons/hi2";
import { motion, AnimatePresence } from "framer-motion";

// Terms and Conditions Dialog
function TermsDialog({ isOpen, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white text-black rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 md:p-8"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Terms & Conditions</h2>
              <div className="space-y-4 text-sm md:text-base text-gray-700">
                <p><strong>Last Updated:</strong> December 2024</p>
                <h3 className="font-bold text-black mt-4">1. Acceptance of Terms</h3>
                <p>By accessing this website, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use this site.</p>
                <h3 className="font-bold text-black mt-4">2. Medical Disclaimer</h3>
                <p>This website provides general information about Mounjaro (tirzepatide) for educational purposes only. It is not intended to replace professional medical advice, diagnosis, or treatment. Always consult your healthcare provider before starting any medication.</p>
                <h3 className="font-bold text-black mt-4">3. Prescription Requirement</h3>
                <p>Mounjaro is a prescription medication. It can only be obtained through a licensed healthcare provider after a proper medical evaluation.</p>
                <h3 className="font-bold text-black mt-4">4. Intellectual Property</h3>
                <p>All content on this website, including text, graphics, and images, is the property of this website or its content suppliers and is protected by copyright laws.</p>
                <h3 className="font-bold text-black mt-4">5. Limitation of Liability</h3>
                <p>We are not liable for any damages arising from the use of this website or reliance on any information provided herein.</p>
              </div>
              <button
                onClick={onClose}
                className="mt-8 w-full py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Privacy Policy Dialog
function PrivacyDialog({ isOpen, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white text-black rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 md:p-8"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Privacy Policy</h2>
              <div className="space-y-4 text-sm md:text-base text-gray-700">
                <p><strong>Last Updated:</strong> December 2024</p>
                <h3 className="font-bold text-black mt-4">1. Information We Collect</h3>
                <p>We may collect personal information such as your email address when you subscribe to our newsletter. We do not sell or share your personal information with third parties.</p>
                <h3 className="font-bold text-black mt-4">2. How We Use Your Information</h3>
                <p>Your email address is used solely to send you updates, health tips, and information about Mounjaro. You can unsubscribe at any time.</p>
                <h3 className="font-bold text-black mt-4">3. Cookies</h3>
                <p>We use cookies to enhance your browsing experience. These cookies help us understand how you use our website so we can improve it.</p>
                <h3 className="font-bold text-black mt-4">4. Data Security</h3>
                <p>We implement appropriate security measures to protect your personal information from unauthorized access, alteration, or disclosure.</p>
                <h3 className="font-bold text-black mt-4">5. Your Rights</h3>
                <p>You have the right to access, correct, or delete your personal information. Contact us at support@mounjaro.com for any privacy-related requests.</p>
              </div>
              <button
                onClick={onClose}
                className="mt-8 w-full py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Footer() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    setEmailError("");

    if (!email.trim()) {
      setEmailError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Start loading
    setIsLoading(true);

    // Simulate 2 second API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSuccess(true);
      setEmail("");

      // Reset success state after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    }, 2000);
  };

  return (
    <footer className="bg-[#2d3339] text-white py-12 md:py-16 overflow-hidden">
      <TermsDialog isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyDialog isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />

      <h2 className="text-[50px] sm:text-[80px] md:text-[100px] lg:text-[220px] font-bold mb-8 md:mb-12 leading-none text-center">
        CONTACT US.
      </h2>
      <div className="mx-auto px-6 md:px-20">
        <div className="flex flex-col md:flex-row md:justify-between gap-12 md:gap-32">
          {/* Left Section */}
          <div className="space-y-6 md:space-y-8 w-full md:w-auto">
            <p className="text-left max-w-xs text-sm md:text-base">
              Stay informed about Mounjaro updates, health tips, and the latest
              research on weight management.
            </p>
            <form onSubmit={handleSubscribe} className="relative flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-2">
                <div className="w-full max-w-xs">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    placeholder="email address"
                    className={`bg-transparent w-full py-2 border-b ${
                      emailError ? "border-red-500" : "border-gray-600"
                    } placeholder-gray-400 focus:outline-none focus:border-white transition-colors`}
                    disabled={isLoading || isSuccess}
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={isLoading || isSuccess}
                  className={`flex items-center justify-center gap-2 text-xs text-nowrap border rounded-full px-4 py-2 min-w-[120px] transition-colors ${
                    isSuccess
                      ? "bg-green-500 border-green-500 text-white"
                      : "border-white hover:bg-white hover:text-[#2d3339]"
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  <AnimatePresence mode="wait">
                    {isLoading ? (
                      <motion.div
                        key="spinner"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                      />
                    ) : isSuccess ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-1"
                      >
                        <HiCheck className="text-base" />
                        <span>Subscribed!</span>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="default"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <span>Subscribe</span>
                        <HiArrowLongRight className="text-base" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
              <AnimatePresence>
                {emailError && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-red-400 text-xs"
                  >
                    {emailError}
                  </motion.p>
                )}
              </AnimatePresence>
            </form>
          </div>

          {/* Right Section */}
          <div className="flex flex-row gap-8 sm:gap-16 mt-8 md:mt-0">
            <div className="text-left">
              <h3 className="font-bold text-sm md:text-base mb-4">
                Navigation
              </h3>
              <ul className="space-y-2 text-gray-400 text-sm md:text-base">
                <li>
                  <button
                    onClick={() => setShowTerms(true)}
                    className="hover:text-white transition-colors text-left"
                  >
                    Terms & Conditions
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowPrivacy(true)}
                    className="hover:text-white transition-colors text-left"
                  >
                    Privacy
                  </button>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-sm md:text-base mb-4">Socials</h3>
              <ul className="space-y-2 text-gray-400 text-sm md:text-base">
                <li>
                  <a
                    href="https://www.linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    Twitter
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;